
import { Home, NavigateNext } from "@mui/icons-material";
import { Box, Typography, Breadcrumbs, Chip, useMediaQuery, useTheme, styled } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider/AuthProvider";
import { useBillingCompanyContext } from "../../../providers";
import { IStoreState } from "../../../redux/initialStoreState";
import { IBreadcrumbProps } from "./IBreadcrumbProps";


export const Breadcrumb: React.FC<IBreadcrumbProps> = ({
  subtitle,
  items,

  rightComponent,
  heading,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();
  const activeMode = useSelector(
    (state: IStoreState) => state.customizerReducer.activeMode,
  );

  const {
    user: { affilated_company_outlet_uuids },
  } = useAuth();
  const userBillingInfo = useBillingCompanyContext();
  const [openCompanyInfoDialog, setCompanyInfoDialog] =
    React.useState<boolean>(false);

  // Filter out first item if it's "dashboard" and transform titles
  const processedItems = React.useMemo(() => {
    if (!items || items.length === 0) return [];

    let filteredItems = items;

    // Remove first item if it's "dashboard" (case-insensitive)
    if (items.length > 0 && items[0].title?.toLowerCase() === "dashboard") {
      filteredItems = items.slice(1);
    }

    // Transform titles: first letter uppercase, rest lowercase
    return filteredItems.map(item => ({
      ...item,
      title: item.title
        ? item.title.charAt(0).toUpperCase() + item.title.slice(1).toLowerCase()
        : item.title
    }));
  }, [items]);


  return (
    <Box display={"flex"} flexDirection={"column"}>
      {heading && (
        <Box sx={{ mb: { xs: 0.5, sm: 1 } }}>
          <Typography
            variant="h3"
            fontWeight="600"
            color="textPrimary"
            // sx={{ fontSize: { xs: "18px", sm: "24px" } }}
          >
            {heading}
          </Typography>
        </Box>
      )}

      <Box
        display="flex"
        sx={{
          mb: 0.5,
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 0.5, sm: 0 },
        }}
      >
        {/* Breadcrumb Container with Light Gray Background */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0.5, sm: 0.5 },
          }}
        >
          <Box sx={{ flex: 1, width: { xs: "100%", sm: "auto" }, minWidth: 0 }}>
            {subtitle && (
              <Typography
                color="textSecondary"
                fontWeight="400"
                variant="h4"
                sx={{
                  mb: 1,
                  fontSize: { xs: "14px", sm: "16px" },
                }}
              >
                {subtitle}
              </Typography>
            )}

            {/* Styled Breadcrumb Container */}
            <Box
              sx={{
                backgroundColor: activeMode === "dark" ? "#20232a" : "#f5f5f5",
                borderRadius: { xs: 1, sm: 2 },
                padding: { xs: "6px 12px", sm: "4px 8px" },
                display: "flex",
                alignItems: "center",
                minHeight: { xs: "36px", sm: "40px" },
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Breadcrumbs
                separator={<NavigateNext fontSize="small" sx={{ color: "text.secondary", fontSize: 16 }} />}
                aria-label="breadcrumb"
                maxItems={isDesktop ? undefined : 2}
                sx={{
                  width: "100%",
                  "& .MuiBreadcrumbs-separator": {
                    margin: { xs: "0 4px", sm: "0 6px" },
                  },
                  "& .MuiBreadcrumbs-ol": {
                    flexWrap: { xs: "nowrap", sm: "wrap" },
                    alignItems: "center",
                  },
                  "& .MuiBreadcrumbs-li": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: { xs: "nowrap", sm: "normal" },
                    maxWidth: { xs: "120px", sm: "none" },
                  },
                }}
              >
                {/* Home / Dashboard Item */}
                {processedItems.length > 0 ? (
                  <Box
                    onClick={() => navigate("/")}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: "#000000",
                      color: "#ffffff",
                      borderRadius: "6px",
                      padding: { xs: "2px 8px", sm: "3px 10px" },
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "#222222",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "11px", sm: "13px" },
                        color: "#ffffff",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Dashboard
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    <Home
                      sx={{
                        fontSize: { xs: 13, sm: 13 },
                        color: "text.secondary",
                        mr: 0.25,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      color="textPrimary"
                      variant="body2"
                      sx={{
                        fontSize: { xs: "11px", sm: "14px" },
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Dashboard
                    </Typography>
                  </Box>
                )}

                {processedItems.map((item, index) => {
                  const isPrevious = index < processedItems.length - 1 || Boolean(item.to);

                  return (
                    <Box
                      key={item.title || index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minWidth: 0,
                      }}
                    >
                      {isPrevious ? (
                        <Box
                          onClick={() => item.to && navigate(item.to)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            backgroundColor: "#000000",
                            color: "#ffffff",
                            borderRadius: "6px",
                            padding: { xs: "2px 8px", sm: "3px 10px" },
                            cursor: item.to ? "pointer" : "default",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": item.to ? {
                              backgroundColor: "#222222",
                            } : {},
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "11px", sm: "13px" },
                              color: "#ffffff",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          color="textPrimary"
                          variant="body2"
                          sx={{
                            fontSize: { xs: "11px", sm: "14px" },
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.title}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Breadcrumbs>
            </Box>
          </Box>

          {/* Right side components - stacked on mobile */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >

            {rightComponent && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: { xs: "100%", sm: "auto" },
                  "& > *": {
                    width: { xs: "100%", sm: "auto" },
                  },
                }}
              >
                {rightComponent}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const StyledBreadcrumb = styled(Chip)(({ theme }: any) => ({
  backgroundColor: "transparent",
  height: "auto",
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightRegular,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "& .MuiChip-label": {
    padding: "4px 8px",
  },
})) as typeof Chip;

